import { datadogRum } from '@datadog/browser-rum';
import { HttpStatusCode } from 'axios';
import { BehaviorSubject } from 'rxjs';
const systemProfile = {
    id: '00000000-0000-0000-0000-000000000000',
    firstName: 'System',
    lastName: '',
    fullName: 'System',
    email: '',
    active: true,
    authorities: [],
    isAuthV2Enabled: false,
    notificationPreferences: { textOk: false, emailOk: false },
    metadata: {
        createdAt: new Date('01/01/2019').toISOString(),
        lastUpdatedAt: new Date('01/01/2019').toISOString(),
        createdByUserID: '00000000-0000-0000-0000-000000000000',
        lastUpdatedByUserID: '00000000-0000-0000-0000-000000000000',
    },
};
class ProfileService {
    axiosInstance;
    static auth;
    // Current User
    static currentProfileSubject = new BehaviorSubject(null);
    static currentUserProfile = null;
    // Current Company Roster
    static rosterSubject = new BehaviorSubject([]);
    static rosterLookup = {};
    static roster = [];
    // Teams (for the current user's organization)
    static teamsSubject = new BehaviorSubject([]);
    static teams = [];
    static apps = {
        adminPortal: {
            roster: [],
            idToProfileMap: {},
        },
    };
    static associatedHaulerIdLookup = {};
    static config;
    constructor(auth, axiosInstance, options) {
        this.axiosInstance = axiosInstance;
        ProfileService.auth = auth;
        ProfileService.auth.currentAuthority.subscribe((authority) => {
            if (authority) {
                this.fetchProfile();
                // Teams are admin-portal only; the backend scopes them to the caller's org from the token.
                if (authority.application === 'aap') {
                    this.fetchTeams();
                }
                if (options?.settings.defaultApp === 'wayste') {
                    this.wayste.haulerAdmin.query();
                }
            }
            else {
                ProfileService.currentProfileSubject.next(null);
                ProfileService.rosterSubject.next([]);
                ProfileService.teamsSubject.next([]);
            }
        });
        if (options) {
            ProfileService.config = options;
        }
    }
    /**
     * Fetches the current user's profile from the server.
     * @returns The current user's profile.
     */
    async fetchProfile() {
        const res = await this.axiosInstance.mainInstance.get('/profile');
        ProfileService.currentUserProfile = res.data;
        datadogRum.setUser({ id: res.data.id, email: res.data.email || undefined });
        ProfileService.currentProfileSubject.next(res.data);
        return res.data;
    }
    /**
     * Updates the caller's own profile. The server takes the profile from the auth token, so this
     * cannot touch anyone else's.
     */
    async updateProfile(input) {
        const res = await this.axiosInstance.mainInstance.put('/profile', input);
        ProfileService.currentUserProfile = res.data;
        ProfileService.currentProfileSubject.next(res.data);
        return res.data;
    }
    get currentUserProfile() {
        return ProfileService.currentUserProfile;
    }
    get currentUserProfileObservable() {
        return ProfileService.currentProfileSubject.asObservable();
    }
    /**
     * Fetches the teams for the caller's organization. The backend scopes the result to the
     * authenticated user's organization (from their token), so no organization id is passed.
     */
    async getTeams() {
        const res = await this.axiosInstance.mainInstance.get('/profile/teams');
        return res.data ?? [];
    }
    /**
     * Fetches and caches the teams for the caller's organization (scoped server-side via the
     * authenticated token). Called once when an admin-portal authority is active.
     */
    async fetchTeams() {
        const teams = await this.getTeams();
        ProfileService.teams = teams;
        ProfileService.teamsSubject.next(teams);
        return teams;
    }
    get teams() {
        return ProfileService.teams;
    }
    get teamsObservable() {
        return ProfileService.teamsSubject.asObservable();
    }
    appAdmin = {
        /**
         * Creates the first Wayste user for a hauler still being onboarded. Rejected once the hauler
         * runs its own account, from which point its admins add their own users.
         *
         * Sent on the app-admin token because this app's own token only carries hauler-admin, which
         * the route does not accept.
         */
        createHaulerUser: async (haulerId, input) => {
            const response = await this.axiosInstance.appAdminInstance.post(`/profile/admin-portal/hauler/${haulerId}/user`, input);
            // MultiStatus (207): user created, but the invitation email failed to send.
            return { profile: response.data, invitationSent: response.status !== HttpStatusCode.MultiStatus };
        },
        setPassword: async (profileID, payload) => {
            const { data } = await this.axiosInstance.appAdminInstance.put(`/profile/hauler-admin/${profileID}/password`, payload);
            return data;
        },
        /**
         * Generates a fresh set-password link for the user and sends it to them. The previous link, if any,
         * is not recoverable, and their current password keeps working until the new one is redeemed.
         */
        sendInvitation: async (profileID) => {
            const { data } = await this.axiosInstance.appAdminInstance.post(`/profile/hauler-admin/${profileID}/invite`);
            return data;
        },
    };
    adminPortal = {
        fetchRoster: async () => {
            const response = await this.axiosInstance.mainInstance.get('/profile/admin-portal/roster');
            ProfileService.apps.adminPortal.roster = response.data || [];
            ProfileService.apps.adminPortal.idToProfileMap = ProfileService.apps.adminPortal.roster.reduce((profileHashmap, profile) => {
                profileHashmap[profile.id] = {
                    ...profile,
                    fullName: profile.fullName || `${profile.firstName} ${profile.lastName}`,
                };
                return profileHashmap;
            }, { [systemProfile.id]: systemProfile });
            ProfileService.roster = ProfileService.apps.adminPortal.roster;
            ProfileService.rosterLookup = ProfileService.apps.adminPortal.idToProfileMap;
            return response.data;
        },
        getRosterFromMemory: () => {
            return ProfileService.apps.adminPortal;
        },
        create: async (input) => {
            const response = await this.axiosInstance.appAdminInstance.post('/profile/admin-portal', input);
            // MultiStatus (207) HTTP status code: the user was created, but the set-password invite email failed to send.
            return { profile: response.data, inviteEmailSent: response.status !== HttpStatusCode.MultiStatus };
        },
        update: async (profileId, input) => {
            const response = await this.axiosInstance.appAdminInstance.patch(`/profile/admin-portal/${profileId}`, input);
            return response.data;
        },
        /**
         * Creates the first Wayste user for a hauler still being onboarded. Rejected once the hauler
         * runs its own account, from which point its admins add their own users.
         */
        createHaulerUser: async (haulerId, input) => {
            const response = await this.axiosInstance.mainInstance.post(`/profile/admin-portal/hauler/${haulerId}/user`, input);
            // MultiStatus (207): user created, but the invitation email failed to send.
            return { profile: response.data, invitationSent: response.status !== HttpStatusCode.MultiStatus };
        },
        fetchProfilesByHaulerId: async (haulerId) => {
            const response = await this.axiosInstance.mainInstance.get(`/profile/admin-portal/hauler/${haulerId}`);
            return response.data || [];
        },
    };
    wayste = {
        haulerAdmin: {
            query: async () => {
                const { data } = await this.axiosInstance.mainInstance.get('/profile/hauler-admin');
                if (data.length > 0) {
                    data.push(systemProfile);
                }
                ProfileService.roster = data;
                ProfileService.rosterLookup = ProfileService.roster.reduce((profileHashmap, profile) => {
                    profileHashmap[profile.id] = profile;
                    ProfileService.associatedHaulerIdLookup[profile.id] = profile;
                    profile.authorities
                        .filter((authority) => authority.haulerID)
                        .map((authority) => authority.haulerID)
                        // @ts-expect-error - TS is not recognizing that the filter above ensures that haulerID is not null
                        .forEach((y) => (ProfileService.associatedHaulerIdLookup[y] = profile));
                    return profileHashmap;
                }, {});
                ProfileService.rosterSubject.next(ProfileService.roster);
                return data;
            },
            fetch: async (profileID) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/profile/hauler-admin/${profileID}`);
                return data;
            },
            update: async (profileID, payload) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/profile/hauler-admin/${profileID}`, payload);
                return data;
            },
            create: async (payload) => {
                const response = await this.axiosInstance.mainInstance.post('/profile/hauler-admin', payload);
                // 207 means the user exists but their set-password invitation could not be delivered,
                // so they cannot sign in until it is re-sent — the caller needs to say so.
                return { profile: response.data, invitationSent: response.status !== 207 };
            },
            delete: async (profileID) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/profile/hauler-admin/${profileID}`);
                return data;
            },
            setPassword: async (profileID, payload) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/profile/hauler-admin/${profileID}/password`, payload);
                return data;
            },
            /**
             * Generates a fresh set-password link for the user and sends it to them. The previous link, if any,
             * is not recoverable, and their current password keeps working until the new one is redeemed.
             */
            sendInvitation: async (profileID) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/profile/hauler-admin/${profileID}/invite`);
                return data;
            },
        },
    };
    get rosterObservable() {
        return ProfileService.rosterSubject.asObservable();
    }
    get rosterMap() {
        return ProfileService.rosterLookup;
    }
    get roster() {
        return ProfileService.roster;
    }
    get employees() {
        return ProfileService.roster.filter((profile) => profile.id !== systemProfile.id && profile.active);
    }
    get accountExecutives() {
        return this.employees
            .filter((employee) => employee.authorities.flatMap((a) => a.roles).some((role) => ['contractor-sales', 'commercial-sales'].includes(role)))
            .map((employee) => ({
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
        }));
    }
    get accountManagers() {
        return this.employees
            .filter((employee) => employee.authorities
            .flatMap((a) => a.roles)
            .some((role) => ['contractor-account-managers', 'commercial-account-managers'].includes(role)))
            .map((employee) => ({
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
        }));
    }
    async fetchProfilesByCustomerId(customerId) {
        const res = await this.axiosInstance.mainInstance.get('/profile/admin-portal/' + customerId);
        return res.data.results;
    }
    /**
     * Emails the current user a short-lived link to change their own password. Auth0 has no API that
     * changes a password after verifying the current one, so redeeming an emailed link stands in for an
     * inline old-password form. The backend reads the target profile from the caller's token, so there
     * is nothing to pass.
     */
    async sendPasswordChangeLink() {
        await this.axiosInstance.mainInstance.post('/profile/password-change-link');
    }
    async adminResetPassword(profileID, newPassword) {
        await this.axiosInstance.mainInstance.post(ProfileService.config.API.baseApiUrl + `/profile/admin-portal/${profileID}/reset-password`, { newPassword });
        return;
    }
    async announcementViewed(announcementID) {
        await this.axiosInstance.mainInstance.post(ProfileService.config.API.baseApiUrl + `/profile/announcement/${announcementID}`);
    }
    /**
     * Given an id, return the corresponding roster entry based on local storage.
     * @param {string} id - The id of the roster entry you want to get.
     * @returns The rosterLookup object.
     */
    get(id) {
        return ProfileService.rosterLookup[id];
    }
    getAssociatedHaulerIds(haulerIdOrDriverProfileId) {
        return ProfileService.associatedHaulerIdLookup[haulerIdOrDriverProfileId];
    }
    /**
     * It returns a list of profiles, optionally filtered by role from local storage
     * @param {string} [role] - string
     * @returns An array of profiles.
     */
    list(role) {
        const res = [...ProfileService.roster];
        return role ? res.filter((profile) => profile.authorities.flatMap((a) => a.roles).includes(role)) : res;
    }
    /**
     * Shortcut to return all profiles with the role of driver as it is used frequently.
     *
     * The function is called drivers() because it returns a list of drivers
     * @returns A list of all driver profiles.
     */
    drivers() {
        return this.list('DRIVER');
    }
}
export default ProfileService;
//# sourceMappingURL=Profile.service.js.map